import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack, IoHomeOutline, IoCube, IoBagOutline, IoMenuOutline } from "react-icons/io5";

const StatusSeparator = ({ text }) => (
  <div className="flex items-center gap-3">
    <div className="h-px flex-1 bg-gray-300" />
    <span className="text-sm font-medium text-gray-600">{text}</span>
    <div className="h-px flex-1 bg-gray-300" />
  </div>
);

const ProductCard = ({ product, onClick }) => (
  <div
    className="flex gap-3 rounded-lg bg-white p-4 shadow-sm cursor-pointer"
    onClick={onClick}
  >
    <img
      loading="lazy"
      src={product.productImages && product.productImages[0] ? product.productImages[0] : ''}
      alt={product.productName}
      className="h-20 w-20 rounded-lg object-cover"
    />
    <div className="flex-1 text-left">
      <p className="text-sm text-gray-900">{product.productName}</p>
      <p className="mt-1 text-sm text-gray-600">{product.productDescription}</p>
      <p className="mt-1 text-sm font-bold text-gray-900">₦{Number(product.productSellingPrice || 0).toLocaleString("en-NG")}</p>
      <p className="mt-1 text-sm text-gray-600">{product.productCategory}</p>
    </div>
  </div>
);

const ProductScreen = () => {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [fetchedProducts, setFetchedProducts] = useState({
    all: {},
    filtered: [],
    loading: false,
    error: null,
  });

  // Placeholder: Replace with your actual JWT token retrieval logic
  const vendorToken = localStorage.getItem("vendorToken") || "<vendor JWT token>";

  useEffect(() => {
    const fetchProducts = async () => {
      setFetchedProducts((prev) => ({ ...prev, loading: true, error: null }));
      let url = `${process.env.REACT_APP_API_URL}/api/vendors/products`;
      if (selectedFilter !== "All") {
        url += `?status=${selectedFilter.toLowerCase()}`;
      }
      try {
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${vendorToken}`,
          },
        });
        const data = await res.json();
        // console.log removed
        if (!res.ok) throw new Error(data.message || "Failed to fetch products");
        if (selectedFilter === "All") {
          // console.log removed
          setFetchedProducts((prev) => ({
            ...prev,
            all: {
              approved: data.approved || [],
              pending: data.pending || [],
              declined: data.declined || [],
              live: data.live || [],
            },
            filtered: [],
            loading: false,
          }));
        } else {
          // console.log removed
          setFetchedProducts((prev) => ({
            ...prev,
            filtered: data.data || [],
            loading: false,
            error: null,
          }));
        }
      } catch (error) {
        setFetchedProducts((prev) => ({ ...prev, loading: false, error: error.message }));
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  const statusKeys = ["approved", "pending", "declined", "live"];
  const statusLabels = {
    approved: "Approved",
    pending: "Pending",
    declined: "Declined",
    live: "Live",
  };
  const filters = ["All", ...statusKeys.map((k) => statusLabels[k])];

  // Use fetchedProducts.filtered for filtered view, or fetchedProducts.all for 'All'

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="fixed top-0 left-0 right-0 z-10 bg-white shadow-sm">
        <div className="flex items-center gap-2 px-6 py-4">
          <button onClick={() => navigate(-1)} className="text-gray-900" aria-label="Go back">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <IoArrowBack className="h-6 w-6 text-black" />
              <svg className="h-6 w-6" style={{ display: 'none' }} viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </span>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Products</h1>
        </div>
        <div className="overflow-x-auto px-6 pb-3">
          <div className="flex gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition ${
                  selectedFilter === filter
                    ? "border border-[#A3E7C1] bg-[#19C463] text-white"
                    : "border border-gray-300 bg-white text-gray-700"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="px-6 pt-32 pb-24">


        {fetchedProducts.loading ? (
          <div className="text-center text-gray-500">Loading products...</div>
        ) : fetchedProducts.error ? (
          <div className="text-center text-red-500">{fetchedProducts.error}</div>
        ) : selectedFilter === "All" ? (
          <>
            {statusKeys.map((status) =>
              Array.isArray(fetchedProducts.all[status]) && fetchedProducts.all[status].length > 0 ? (
                <div key={status} className="mb-8">
                  <StatusSeparator text={statusLabels[status]} />
                  <div className="mt-6 space-y-4">
                    {fetchedProducts.all[status].map((product) => (
                      <ProductCard
                        key={product._id}
                        product={product}
                        onClick={() => navigate(`/view-single-product/${product._id}`)}
                      />
                    ))}
                  </div>
                </div>
              ) : null
            )}
          </>
        ) : (
          <div className="mt-6 space-y-4">
            {fetchedProducts.filtered.length === 0 ? (
              <div>No products found.</div>
            ) : (
              fetchedProducts.filtered.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                        onClick={() => navigate(`/view-single-product/${product._id}`)}
                />
              ))
            )}
          </div>
        )}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 flex items-center justify-around border-t border-gray-200 bg-white py-3">
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-home")}>
          <IoHomeOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Home</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/products")}>
          <IoCube className="h-6 w-6 text-[#19C463]" />
          <span className="text-xs font-bold text-[#19C463]">Product</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/vendor-orders")}>
          <IoBagOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Orders</span>
        </button>
        <button className="flex flex-col items-center gap-1" onClick={() => navigate("/menu")}>
          <IoMenuOutline className="h-6 w-6 text-gray-500" />
          <span className="text-xs text-gray-500">Menu</span>
        </button>
      </nav>
    </div>
  );
};

export default ProductScreen;


